import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getUserPreferences, updateUserPreferences } from '../../services/api';
import { getFinancialYears } from '../../services/api';
import { usePreferences } from '../../context/PreferencesContext';
import styles from './UserPreferencesPage.module.css';

const THEME_OPTIONS = [
  { id: 'saffron',   label: 'Saffron',    color: '#e65100' },
  { id: 'blue-dark', label: 'Blue Dark',  color: '#1565c0' },
  { id: 'emerald',   label: 'Emerald',    color: '#2e7d32' },
  { id: 'black',     label: 'Midnight',   color: '#111111' },
];

const PAYMENT_MODES = [
  { value: '',       label: 'No preference' },
  { value: 'cash',   label: 'Cash' },
  { value: 'bank',   label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
];

const PAGE_SIZES = [10, 25, 50, 100];

const UserPreferencesPage = () => {
  const { activeDerasarId } = useAuth();
  const { setTheme } = useTheme();
  const { refreshPreferences } = usePreferences();

  const [form, setForm]       = useState({ theme: 'saffron', defaultPaymentMode: '', tablePageSize: 25, defaultFinancialYearId: '' });
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const patch = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prefRes, fyRes] = await Promise.all([
        getUserPreferences(),
        activeDerasarId ? getFinancialYears(activeDerasarId) : Promise.resolve({ data: { data: [] } }),
      ]);
      const pref = prefRes.data?.data || prefRes.data || {};
      const fys  = fyRes.data?.data   || fyRes.data  || [];
      setForm({
        theme:                  pref.theme                  || 'saffron',
        defaultPaymentMode:     pref.defaultPaymentMode     || '',
        tablePageSize:          pref.tablePageSize          ?? 25,
        defaultFinancialYearId: pref.defaultFinancialYearId ? String(pref.defaultFinancialYearId) : '',
      });
      setFinancialYears(fys.filter((fy) => !fy.isOpeningYear));
    } catch {
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserPreferences({
        theme:                  form.theme,
        defaultPaymentMode:     form.defaultPaymentMode  || null,
        tablePageSize:          Number(form.tablePageSize),
        defaultFinancialYearId: form.defaultFinancialYearId || null,
      });
      setTheme(form.theme);
      await refreshPreferences();
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>;

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h2>User Preferences</h2>
      </div>
      <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: 20, marginTop: -16 }}>
        Personalise your experience. These settings apply only to your account.
      </p>

      <div className={styles.sections}>

        {/* Theme */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Theme</h3>
            <p>Choose your colour scheme.</p>
          </div>
          <div className={styles.themeGrid}>
            {THEME_OPTIONS.map((t) => (
              <button
                key={t.id}
                className={`${styles.themeCard} ${form.theme === t.id ? styles.themeCardActive : ''}`}
                onClick={() => patch('theme', t.id)}
              >
                <span className={styles.themeSwatch} style={{ background: t.color }} />
                <span className={styles.themeLabel}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Accounting defaults */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Accounting Defaults</h3>
            <p>Pre-fill common fields when creating new entries.</p>
          </div>

          <div className={styles.fieldRow}>
            <label>Default Payment Mode</label>
            <select value={form.defaultPaymentMode} onChange={(e) => patch('defaultPaymentMode', e.target.value)}>
              {PAYMENT_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          {activeDerasarId && financialYears.length > 0 && (
            <div className={styles.fieldRow}>
              <label>Default Financial Year</label>
              <select value={form.defaultFinancialYearId} onChange={(e) => patch('defaultFinancialYearId', e.target.value)}>
                <option value="">No preference (use current)</option>
                {financialYears.map((fy) => (
                  <option key={fy.id} value={String(fy.id)}>{fy.yearLabel}{fy.isClosed ? ' (closed)' : ''}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Display */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Display</h3>
            <p>Control how data is presented in tables.</p>
          </div>
          <div className={styles.fieldRow}>
            <label>Rows per page</label>
            <select value={form.tablePageSize} onChange={(e) => patch('tablePageSize', Number(e.target.value))}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} rows</option>)}
            </select>
          </div>
        </div>

      </div>

      <div className={styles.actions}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default UserPreferencesPage;
